/* Wargame Portal 3.0.25 — shared storefront behavior with animated drill-down navigation. No external runtime. */
(() => {
  'use strict';
  const root = document.documentElement;
  let config = {};
  try { config = JSON.parse(document.getElementById('WGPConfig')?.textContent || '{}'); } catch (_) {}
  const Portal = window.Portal = {
    config,
    announce(message) { const el = document.getElementById('PortalLiveRegion'); if (el) el.textContent = message; },
    async request(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', ...options });
      if (!response.ok) {
        let data; try { data = await response.json(); } catch (_) {}
        throw new Error(data?.description || data?.message || `Request failed (${response.status}). Please try again.`);
      }
      return response;
    },
    async post(url, body) {
      const response = await this.request(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      return response.json();
    },
    setError(container, message) {
      const el = container?.querySelector('[data-form-error], [data-variant-error]');
      if (el) { el.textContent = message || ''; el.hidden = !message; }
    }
  };

  const theme = () => root.dataset.theme || (matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
  function paintTheme() {
    const light = theme() === 'light';
    document.querySelectorAll('[data-toggle-theme]').forEach(button => {
      const moon = button.querySelector('[data-icon-dark]'); const sun = button.querySelector('[data-icon-light]');
      if (moon) moon.hidden = light;
      if (sun) sun.hidden = !light;
      button.setAttribute('aria-pressed', String(light));
    });
  }
  matchMedia('(prefers-color-scheme: light)').addEventListener('change', paintTheme);

  const dialogTriggers = new WeakMap();
  const menuTransitions = new WeakMap();
  const menuMotion = matchMedia('(prefers-reduced-motion: reduce)');
  function syncScrollLock() { root.classList.toggle('dialog-open', !!document.querySelector('dialog[open]')); }
  function panelById(dialog, id) {
    return Array.from(dialog.querySelectorAll('[data-menu-panel]')).find(el => el.dataset.menuPanel === id);
  }
  function settlePanel(dialog, target, focus = true) {
    // Move focus into the destination before hiding the panel that owned it.
    // This avoids stranding focus inside a newly hidden subtree after Back/Next.
    if (target) {
      target.hidden = false;
      target.removeAttribute('aria-hidden');
      if (focus) (target.querySelector('button, a') || dialog.querySelector('[data-dialog-close]'))?.focus({ preventScroll: true });
    }
    dialog.querySelectorAll('[data-menu-panel]').forEach(panel => {
      panel.hidden = panel !== target;
      panel.removeAttribute('aria-hidden');
      panel.style.removeProperty('transform');
      panel.style.removeProperty('opacity');
    });
    dialog.dataset.activePanel = target?.dataset.menuPanel || '';
    dialog.removeAttribute('data-menu-transitioning');
    dialog.querySelector('.menu-dialog__body')?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }
  function cancelMenuTransition(dialog) {
    const running = menuTransitions.get(dialog);
    if (!running) return;
    running.animations.forEach(animation => animation.cancel());
    menuTransitions.delete(dialog);
    dialog.removeAttribute('data-menu-transitioning');
  }
  function showPanel(dialog, id, focus = true, direction = 'none') {
    if (!dialog) return;
    const target = panelById(dialog, id);
    if (!target) return;
    cancelMenuTransition(dialog);
    const currentId = dialog.dataset.activePanel || dialog.querySelector('[data-menu-panel]:not([hidden])')?.dataset.menuPanel;
    const current = currentId ? panelById(dialog, currentId) : null;
    const body = dialog.querySelector('.menu-dialog__body');
    if (!current || current === target || direction === 'none' || menuMotion.matches || typeof target.animate !== 'function') {
      settlePanel(dialog, target, focus);
      return;
    }

    body?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    target.hidden = false;
    target.setAttribute('aria-hidden', 'true');
    dialog.dataset.menuTransitioning = direction;

    const distance = Math.min(56, Math.max(34, Math.round((body?.clientWidth || 420) * .11)));
    const forward = direction === 'forward';
    const enterX = forward ? distance : -distance;
    const exitX = forward ? -distance : distance;
    const duration = 285;
    const easing = 'cubic-bezier(.22,.61,.36,1)';
    const options = { duration, easing, fill: 'both' };
    const animations = [
      current.animate([
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(${exitX}px)` }
      ], options),
      target.animate([
        { opacity: 0, transform: `translateX(${enterX}px)` },
        { opacity: 1, transform: 'translateX(0)' }
      ], options)
    ];
    const transition = { animations, target };
    menuTransitions.set(dialog, transition);

    Promise.allSettled(animations.map(animation => animation.finished)).then(() => {
      if (menuTransitions.get(dialog) !== transition) return;
      animations.forEach(animation => animation.cancel());
      menuTransitions.delete(dialog);
      settlePanel(dialog, target, focus);
    });
  }
  function closeDialog(dialog) {
    if (!dialog?.open) return;
    dialog.close();
  }
  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    document.querySelectorAll('dialog[open]').forEach(other => { if (other !== dialog) other.close(); });
    dialogTriggers.set(dialog, trigger || document.activeElement);
    if (dialog.matches('[data-menu-dialog]')) showPanel(dialog, 'root', false);
    if (!dialog.open) dialog.showModal();
    trigger?.setAttribute('aria-expanded', 'true');
    syncScrollLock();
    return true;
  }
  Portal.openDialog = openDialog;
  Portal.closeDialog = closeDialog;
  const boundDialogs = new WeakSet();
  function initDialogs(scope = document) {
    scope.querySelectorAll('dialog[data-dialog]').forEach(dialog => {
      if (boundDialogs.has(dialog)) return;
      boundDialogs.add(dialog);
      dialog.addEventListener('close', () => {
        const trigger = dialogTriggers.get(dialog);
        trigger?.setAttribute('aria-expanded', 'false');
        syncScrollLock();
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      });
      dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const items = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        if (!items.length) { event.preventDefault(); return; }
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      });
      dialog.addEventListener('cancel', event => {
        if (!dialog.matches('[data-menu-dialog]')) return;
        const active = dialog.querySelector('[data-menu-panel]:not([hidden])');
        const back = active?.querySelector('[data-menu-back]');
        if (back) { event.preventDefault(); showPanel(dialog, back.dataset.menuBack, true, 'back'); }
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
      });
    });
  }

  document.addEventListener('click', event => {
    const target = event.target.closest?.('button, a');
    if (!target) return;
    if (target.matches('[data-toggle-theme]')) {
      const next = theme() === 'light' ? 'dark' : 'light';
      root.dataset.theme = next;
      try { localStorage.setItem('wgp-theme', next); } catch (_) {}
      paintTheme();
    }
    if (target.matches('[data-dialog-open]')) {
      const dialog = document.getElementById(target.dataset.dialogOpen);
      if (openDialog(dialog, target)) event.preventDefault();
    }
    if (target.matches('[data-dialog-close]')) closeDialog(target.closest('dialog'));
    if (target.matches('[data-menu-next]')) showPanel(target.closest('dialog'), target.dataset.menuNext, true, 'forward');
    if (target.matches('[data-menu-back]')) showPanel(target.closest('dialog'), target.dataset.menuBack, true, 'back');
    if (target.matches('[data-qty]')) {
      const input = target.closest('.qty')?.querySelector('input[type="number"]');
      if (!input || input.disabled) return;
      const min = Number(input.min || 1), max = input.max ? Number(input.max) : Infinity, step = Number(input.step) || 1;
      const previous = Number(input.value) || min;
      input.value = String(Math.max(min, Math.min(max, previous + (target.dataset.qty === 'up' ? step : -step))));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (target.matches('[data-media-select]')) Portal.selectMedia(target.closest('[data-gallery]'), target.dataset.mediaSelect);
    if (target.matches('[data-slider-step]')) {
      const slider = target.closest('[data-slideshow]');
      if (slider) moveSlide(slider, Number(target.dataset.sliderStep));
    }
  });

  Portal.selectMedia = (gallery, mediaId) => {
    if (!gallery) return;
    const selected = gallery.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!selected) return;
    gallery.querySelectorAll('[data-media-id]').forEach(media => {
      media.hidden = media !== selected;
      if (media.hidden) {
        media.querySelectorAll('video').forEach(video => video.pause());
        media.querySelectorAll('iframe').forEach(frame => { const src = frame.getAttribute('src'); if (src) frame.setAttribute('src', src); });
        media.querySelectorAll('model-viewer').forEach(model => model.pause?.());
      }
    });
    gallery.querySelectorAll('[data-media-select]').forEach(button => button.setAttribute('aria-current', String(button.dataset.mediaSelect === String(mediaId))));
  };

  const searchBound = new WeakSet();
  const searchCache = new Map();
  function initSearch(scope = document) {
    scope.querySelectorAll('[data-predictive-search]').forEach(wrapper => {
      if (searchBound.has(wrapper)) return;
      searchBound.add(wrapper);
      const input = wrapper.querySelector('[data-search-input]');
      const output = wrapper.querySelector('[data-search-results]');
      const status = wrapper.querySelector('[data-search-status]');
      let timer, controller, active = -1;
      const links = () => [...output.querySelectorAll('[data-search-option]')];
      function hide() { output.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; }
      function show(html) {
        output.innerHTML = html; output.hidden = false; active = -1;
        input.setAttribute('aria-expanded', 'true'); input.removeAttribute('aria-activedescendant');
        status.textContent = output.querySelector('[data-results-message]')?.dataset.resultsMessage || '';
      }
      async function search() {
        const query = input.value.trim();
        controller?.abort();
        if (query.length < 2) { hide(); return; }
        if (searchCache.has(query)) { show(searchCache.get(query)); return; }
        controller = new AbortController();
        const url = new URL(config.predictiveSearch || '/search/suggest', location.origin);
        url.searchParams.set('q', query); url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'product,collection,article,page');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        try {
          const response = await Portal.request(url, { signal: controller.signal });
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fragment = html.querySelector('[data-predictive-content]');
          if (!fragment || query !== input.value.trim()) return;
          if (searchCache.size > 20) searchCache.delete(searchCache.keys().next().value);
          searchCache.set(query, fragment.innerHTML);
          if (wrapper.contains(document.activeElement)) show(fragment.innerHTML);
        } catch (error) { if (error.name !== 'AbortError') hide(); }
      }
      input.addEventListener('input', () => { clearTimeout(timer); controller?.abort(); hide(); timer = setTimeout(search, 200); });
      input.addEventListener('focus', () => { if (input.value.trim().length >= 2) search(); });
      input.addEventListener('keydown', event => {
        if (event.key === 'Escape') { hide(); event.stopPropagation(); return; }
        const options = links();
        if (output.hidden || !options.length) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); active = (active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options.forEach((option, index) => option.setAttribute('aria-selected', String(index === active)));
          input.setAttribute('aria-activedescendant', options[active].id);
          options[active].scrollIntoView({ block: 'nearest' });
        }
        if (event.key === 'Enter' && active >= 0) { event.preventDefault(); options[active].click(); }
      });
      wrapper.addEventListener('focusout', () => setTimeout(() => { if (!wrapper.contains(document.activeElement)) hide(); }, 0));
      document.addEventListener('pointerdown', event => { if (!wrapper.contains(event.target)) hide(); });
    });
  }

  function moveSlide(slider, direction) {
    const slides = [...slider.querySelectorAll('[data-slide]')];
    if (!slides.length) return;
    const current = slides.findIndex(slide => !slide.hidden);
    const next = (current + direction + slides.length) % slides.length;
    slides.forEach((slide, i) => { slide.hidden = i !== next; });
    const counter = slider.querySelector('[data-slide-counter]'); if (counter) counter.textContent = `${next + 1} / ${slides.length}`;
  }

  function initAddresses(scope = document) {
    scope.querySelectorAll('[data-country-select]').forEach(select => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      const province = document.getElementById(select.dataset.provinceId);
      if (!province) return;
      function update() {
        let provinces = [];
        try { provinces = JSON.parse(select.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) {}
        const preferred = province.dataset.default || province.value;
        province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value, false, value === preferred || label === preferred)));
        province.closest('[data-province-wrap]').hidden = !provinces.length;
        province.disabled = !provinces.length;
      }
      if (select.dataset.default) [...select.options].forEach(option => { option.selected = option.value === select.dataset.default || option.textContent === select.dataset.default; });
      select.addEventListener('change', () => { province.dataset.default = ''; update(); }); update();
    });
  }
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-address-delete]');
    if (form && !confirm(form.dataset.confirm)) event.preventDefault();
  });
  document.addEventListener('change', event => {
    if (event.target.type !== 'checkbox' || !event.target.id) return;
    document.querySelectorAll('[data-checkbox-fallback]').forEach(input => { if (input.dataset.checkboxFallback === event.target.id) input.disabled = event.target.checked; });
  });

  let headerObserver;
  function init(scope = document) {
    initDialogs(scope); initSearch(scope); initAddresses(scope); paintTheme();
    const header = document.querySelector('[data-sticky-header]');
    if (header && 'ResizeObserver' in window) {
      headerObserver?.disconnect();
      header.closest('.shopify-section')?.classList.toggle('portal-header-section--sticky', header.dataset.stickyHeader === 'true');
      headerObserver = new ResizeObserver(() => root.style.setProperty('--header-height', `${header.dataset.stickyHeader === 'true' ? header.offsetHeight : 0}px`));
      headerObserver.observe(header);
    }
  }
  Portal.init = init;
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', syncScrollLock);
  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-slide]');
    if (slide) { const parent = slide.closest('[data-slideshow]'); parent.querySelectorAll('[data-slide]').forEach(s => { s.hidden = s !== slide; }); }
  });

  function responsiveFilters(scope = document) {
    scope.querySelectorAll('[data-filter-box]').forEach(box => {
      if (box.dataset.responsiveBound) return;
      box.dataset.responsiveBound = 'true';
      const media = matchMedia('(min-width: 990px)');
      const set = () => { box.open = media.matches; };
      set(); media.addEventListener('change', set);
    });
  }
  responsiveFilters();
  document.addEventListener('shopify:section:load', event => responsiveFilters(event.target));

  function revealRecovery() {
    if (location.hash === '#recover') { const recover = document.getElementById('recover'); if (recover) recover.open = true; }
  }
  document.addEventListener('click', event => { if (event.target.closest('a[href="#recover"]')) { const recover = document.getElementById('recover'); if (recover) recover.open = true; } });
  addEventListener('hashchange', revealRecovery); revealRecovery();

  /* Wargame Portal 3.0.26 — generic mobile merchandising rail. */
  if (!customElements.get('wgp-mobile-rail')) {
    class WGPMobileRail extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => { if (this.isConnected && !this._bound) this._init(); });
      }
      _init() {
        this._bound = true;
        this._abort = new AbortController();
        this._track = this.querySelector('[data-mobile-rail-track]');
        this._controls = this.querySelector('[data-mobile-rail-controls]');
        this._counter = this.querySelector('[data-mobile-rail-counter]');
        this._status = this.querySelector('[data-mobile-rail-status]');
        this._media = matchMedia('(max-width: 749px)');
        this._raf = null;
        if (!this._track) return;
        const signal = this._abort.signal;
        this.addEventListener('click', event => {
          const button = event.target.closest('[data-mobile-rail-step]');
          if (!button || !this.contains(button) || button.disabled) return;
          this._step(Number(button.dataset.mobileRailStep) || 0);
        }, { signal });
        this._track.addEventListener('scroll', () => this._schedule(), { signal, passive: true });
        this._track.addEventListener('keydown', event => {
          if (!this._active() || event.target !== this._track) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault(); this._step(event.key === 'ArrowRight' ? 1 : -1);
          }
          if (event.key === 'Home') { event.preventDefault(); this._track.scrollTo({ left: 0, behavior: this._behavior() }); }
          if (event.key === 'End') { event.preventDefault(); this._track.scrollTo({ left: this._track.scrollWidth, behavior: this._behavior() }); }
        }, { signal });
        this._media.addEventListener?.('change', () => { this._track.scrollLeft = 0; this._update(); }, { signal });
        if ('ResizeObserver' in window) {
          this._resize = new ResizeObserver(() => this._schedule());
          this._resize.observe(this._track);
        } else window.addEventListener('resize', () => this._schedule(), { signal, passive: true });
        document.addEventListener('shopify:block:select', event => {
          if (!this._active() || !this.contains(event.target)) return;
          const item = [...this._items()].find(node => node === event.target || node.contains(event.target));
          item?.scrollIntoView({ behavior: this._behavior(), block: 'nearest', inline: 'nearest' });
        }, { signal });
        this._update();
      }
      _active() { return this.dataset.mobileLayout === 'carousel' && this._media?.matches; }
      _items() { return this._track ? [...this._track.children].filter(el => el.getClientRects().length) : []; }
      _behavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
      _schedule() {
        if (this._raf !== null) return;
        this._raf = requestAnimationFrame(() => { this._raf = null; this._update(); });
      }
      _step(direction) {
        if (!this._active() || !direction) return;
        const items = this._items(); if (!items.length) return;
        const gap = parseFloat(getComputedStyle(this._track).columnGap || getComputedStyle(this._track).gap) || 0;
        const distance = items[0].getBoundingClientRect().width + gap;
        this._track.scrollBy({ left: direction * distance, behavior: this._behavior() });
      }
      _update() {
        if (!this._track) return;
        const active = this._active();
        const items = this._items();
        const overflow = active && this._track.scrollWidth > this._track.clientWidth + 2;
        this.dataset.mobileOverflow = String(overflow);
        if (this._controls) this._controls.hidden = !overflow;
        if (active) this._track.tabIndex = 0; else this._track.removeAttribute('tabindex');
        if (!active || !items.length) {
          if (!active && this._track.scrollLeft) this._track.scrollLeft = 0;
          return;
        }
        const trackRect = this._track.getBoundingClientRect();
        const tolerance = 3;
        let first = 0, last = 0, found = false;
        items.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          if (rect.right > trackRect.left + tolerance && rect.left < trackRect.right - tolerance) {
            if (!found) { first = index; found = true; }
            last = index;
          }
        });
        if (!found) first = last = 0;
        const atStart = this._track.scrollLeft <= 2;
        const atEnd = this._track.scrollLeft + this._track.clientWidth >= this._track.scrollWidth - 2;
        const prev = this.querySelector('[data-mobile-rail-step="-1"]');
        const next = this.querySelector('[data-mobile-rail-step="1"]');
        if (prev) prev.disabled = !overflow || atStart;
        if (next) next.disabled = !overflow || atEnd;
        const text = `${first + 1}${last > first ? '–' + (last + 1) : ''} / ${items.length}`;
        if (this._counter) this._counter.textContent = text;
        if (this._status) this._status.textContent = `Showing items ${first + 1} through ${last + 1} of ${items.length}`;
      }
      disconnectedCallback() {
        this._abort?.abort(); this._resize?.disconnect();
        if (this._raf !== null) cancelAnimationFrame(this._raf);
        this._bound = false; this._raf = null;
      }
    }
    customElements.define('wgp-mobile-rail', WGPMobileRail);
  }

})();
